import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { Helmet } from 'react-helmet-async';
// Replaced lucide-react icons with Unicode symbols for React 19 compatibility
import './BlogPage.css';

// Structured data for SEO
const BlogStructuredData = ({ title, description, datePublished, dateModified, imageUrl, authorName, slug }) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "datePublished": datePublished?.toDate?.() ?? new Date(datePublished || Date.now()).toISOString(),
    "dateModified": dateModified?.toDate?.() ?? new Date(dateModified || Date.now()).toISOString(),
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": "CarConsul",
      "logo": {
        "@type": "ImageObject",
        "url": "https://carconsul.com/logo192.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://carconsul.com/blog/${slug}`
    }
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData)}
    </script>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchBlogPost = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Query Firestore for the blog post with matching slug
      const blogPostQuery = query(
        collection(db, 'blogposts'),
        where('slug', '==', slug)
      );
      
      const blogPostSnapshot = await getDocs(blogPostQuery);
      
      if (!blogPostSnapshot.empty) {
        // Blog post found
        const data = {
          id: blogPostSnapshot.docs[0].id,
          ...blogPostSnapshot.docs[0].data()
        };
        
        // Process HTML content to fix image loading issues
        if (data.htmlContent) {
          // Fix image tags to avoid CORS issues
          data.htmlContent = data.htmlContent.replace(
            /<img([^>]*)src="([^"]+)"([^>]*)>/gi,
            '<img$1src="$2"$3 loading="lazy" style="max-width: 100%; height: auto;">'
          );
        }
        
        setBlogPost(data);
      } else {
        // Blog post not found
        setError('Blog post not found');
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setError('Error loading blog post');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] blog-page-container">
      <Helmet>
        <title>{blogPost ? `${blogPost.title} | CarConsul Blog` : 'Blog | CarConsul'}</title>
        <meta name="description" content={blogPost?.excerpt || "Explore car buying insights and tips on the CarConsul Blog."} />
        <meta property="og:title" content={blogPost ? `${blogPost.title} | CarConsul Blog` : 'Blog | CarConsul'} />
        <meta property="og:description" content={blogPost?.excerpt || "Explore car buying insights and tips on the CarConsul Blog."} />
        {blogPost?.imageUrl && <meta property="og:image" content={blogPost.imageUrl} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://carconsul.com/blog/${slug}`} />
        <link rel="canonical" href={`https://carconsul.com/blog/${slug}`} />
        {blogPost && (
          <meta name="keywords" content={blogPost.keywords || "car buying, automotive, vehicle analysis, car consultant"} />
        )}
        {blogPost && BlogStructuredData({
          title: blogPost.title,
          description: blogPost.excerpt || blogPost.description,
          datePublished: blogPost.createdAt,
          dateModified: blogPost.updatedAt || blogPost.createdAt,
          imageUrl: blogPost.imageUrl,
          authorName: blogPost.author || "CarConsul Team",
          slug: slug,
          category: blogPost.category,
          keywords: blogPost.keywords
        })}
      </Helmet>

      
      <main className="flex-grow pt-20">
        {isLoading ? (
          <div className="container mx-auto px-4 py-16 flex justify-center">
            <div className="animate-pulse text-[#BCE6EB] text-xl">Loading...</div>
          </div>
        ) : error ? (
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto bg-red-500 bg-opacity-10 text-red-300 p-6 rounded-xl border border-red-500 border-opacity-20">
              <h2 className="text-xl font-bold mb-2">Error</h2>
              <p>{error}</p>
              <Link to="/blog" className="inline-block mt-4 text-[#BCE6EB] hover:text-[#62B6CB] transition-colors">
                ← Back to Blog
              </Link>
            </div>
          </div>
        ) : blogPost ? (
          <>
            <div className="bg-[#020617] pt-124 pb-4"/>
            <div className="bg-[#020617] pt-124 pb-4">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  
                  {blogPost.category && (
                    <Link 
                      to={`/blog/category/${blogPost.category.toLowerCase().replace(/\s+/g, '-')}`}
                      className="inline-block category-badge mb-3 hover:bg-opacity-25"
                    >
                      {blogPost.category}
                    </Link>
                  )}
                  
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Poppins'] leading-tight">
                    {blogPost.title}
                  </h1>
                  
                  <div className="flex flex-wrap items-center text-blue-200 text-opacity-60 gap-6 mb-6">
                    <div className="flex items-center">
                      📅
                      <span>{formatDate(blogPost.createdAt)}</span>
                    </div>
                    <div className="flex items-center">
                      ⏱️
                      <span>5 min read</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center mb-8 relative group">
                    <button className="flex items-center bg-blue-600 bg-opacity-20 hover:bg-opacity-30 text-blue-100 py-2 px-4 rounded-md transition-colors">
                      📤
                      <span>Share Article</span>
                    </button>
                    <div className="absolute top-full left-0 mt-2 flex space-x-4 bg-[#0F172A] bg-opacity-95 p-4 rounded-md shadow-lg invisible group-hover:visible z-10">
                      <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://firsttimehome.net/blog/${slug}`)}&text=${encodeURIComponent(blogPost.title)}`} target="_blank" rel="noopener noreferrer" className="text-[#BCE6EB] hover:text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                      </a>
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://firsttimehome.net/blog/${slug}`)}`} target="_blank" rel="noopener noreferrer" className="text-[#BCE6EB] hover:text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                      </a>
                      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://firsttimehome.net/blog/${slug}`)}`} target="_blank" rel="noopener noreferrer" className="text-[#BCE6EB] hover:text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {blogPost.imageUrl && (
              <div className="container mx-auto px-4 py-4">
                <div className="max-w-4xl mx-auto">
                  <div className="blog-featured-image-container">
                    <img
                      src={blogPost.imageUrl}
                      alt={`${blogPost.title} - ${blogPost.imageAltDescription || 'Car buying content'}`}
                      className="rounded-xl shadow-lg"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="container mx-auto px-4 py-8">
              <div className="max-w-4xl mx-auto">
                <article className="blog-content prose prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={{ 
                    __html: blogPost.htmlContent 
                      ? blogPost.htmlContent
                        // Process external images to avoid CORS issues and add descriptive alt text
                        .replace(
                          /<img\s+[^>]*src=["']https?:\/\/(?!firsttimehome\.net)[^"']+["'][^>]*>/gi, 
                          (match) => {
                            // Add loading="lazy", error handler, and improved alt text to all img tags
                            let altText = "";
                            const altMatch = match.match(/alt=["']([^"']*)["']/i);
                            if (altMatch && altMatch[1]) {
                              altText = altMatch[1];
                            } else {
                              // If no alt text, create descriptive alt based on context
                              if (match.toLowerCase().includes('mortgage')) {
                                altText = "Mortgage document or home loan";
                              } else if (match.toLowerCase().includes('house') || match.toLowerCase().includes('home')) {
                                altText = "House or home property";
                              } else if (match.toLowerCase().includes('inspection')) {
                                altText = "Home inspection process";
                              } else {
                                altText = `Content related to ${blogPost.title}`;
                              }
                            }
                            
                            // Remove existing alt attribute if present
                            let newMatch = match.replace(/\s+alt=["'][^"']*["']/gi, '');
                            
                            // Add our improved alt text and consistent styling
                            return newMatch
                              .replace(/\s+src=["']([^"']+)["']/i, 
                                ` src="$1" alt="${altText}" loading="lazy" onerror="this.onerror=null; this.src='/placeholder-image.png';"`)
                              .replace(/\s+style=["'][^"']*["']/gi, ' style="max-width:100%; aspect-ratio:16/9; object-fit:cover; border-radius:8px; margin:2em auto; display:block; box-shadow:0 5px 15px rgba(0,0,0,0.3);"');
                          }
                        )
                        // Make all links open in a new tab with noopener noreferrer for security
                        .replace(
                          /<a\s+[^>]*href=["']https?:\/\/[^"']+["'][^>]*>/gi,
                          (match) => {
                            if (!/target=["']_blank["']/i.test(match)) {
                              match = match.replace(/>$/, ' target="_blank" rel="noopener noreferrer">');
                            }
                            return match;
                          }
                        )
                        // Add contextual links to relevant pages
                        .replace(
                          /\b(mortgage pre-approval|pre-approval process)\b/gi,
                          '<a href="/blog/mortgage-pre-approval-process" title="Learn more about the mortgage pre-approval process">$1</a>'
                        )
                        .replace(
                          /\b(first-time (home )?buyer programs|first time (home )?buyer programs)\b/gi,
                          '<a href="/blog/first-time-home-buyer-programs" title="Explore first-time home buyer programs">$1</a>'
                        )
                        .replace(
                          /\b(home inspection checklist|inspection checklist)\b/gi,
                          '<a href="/blog/home-inspection-checklist" title="View our home inspection checklist">$1</a>'
                        )
                        // Improve paragraph text color for better readability
                        .replace(
                          /<p[^>]*>/gi,
                          '<p style="color: #dbeafe;">'
                        )
                        // Improve heading colors
                        .replace(
                          /<h([1-6])[^>]*>/gi,
                          '<h$1 style="color: #ffffff;">'
                        )
                        // Improve link colors
                        .replace(
                          /<a([^>]*)>/gi,
                          '<a$1 style="color: #3b82f6;">'
                        )
                      : ''
                  }} />
                </article>


              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
};

export default BlogPostPage;